import { initFirebase } from '../../firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'

const { db } = initFirebase()

export class FirebaseUserService {
  async getAllUsers() {
    try {
      const usersRef = collection(db, 'users')
      const q = query(usersRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      const users = querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }))

      // Fetch all scans data at once
      const scansRef = collection(db, 'scans')
      const scansQuery = query(scansRef, orderBy('createdAt', 'desc'))
      const scansSnapshot = await getDocs(scansQuery)
      const allScans = scansSnapshot.docs.map(doc => ({
        scanId: doc.id,
        ...doc.data()
      }))

      const chatsRef = collection(db, 'chats');
      const chatsQuery = query(chatsRef, orderBy('createdAt', 'desc'));
      const chatsSnapshot = await getDocs(chatsQuery);
      const allChats = chatsSnapshot.docs.map(doc => ({
        chatId: doc.id,
        ...doc.data()
      }));

      // Process users with their scan data
      const usersWithScansAndChats = users.map((user) => {
        const userScans = allScans.filter(scan => scan.userId === user.uid)
        const userChats = allChats.filter(chat => chat.userId === user.uid);
        const lastChat = userChats.length > 0 ? userChats[0] : null;

        return {
          ...user,
          totalScans: userScans.length,
          lastScanDate: userScans.length > 0 ? userScans[0].createdAt : null,
          totalChats: userChats.length,
          lastChatDate: lastChat ? lastChat.createdAt : null
        }
      })

      return {
        users: usersWithScansAndChats,
        allScans: allScans,
        allChats: allChats
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  }

  calculateDailyStats(users, scans, days = 7) {
    const stats = {
      newUsers: [],
      newScans: []
    }

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      date.setHours(0, 0, 0, 0)
      
      const nextDate = new Date(date)
      nextDate.setDate(nextDate.getDate() + 1)

      // Count new users for this day
      const newUsersCount = users.filter(user => {
        const userDate = user.createdAt?.toDate?.() || new Date(user.createdAt)
        return userDate >= date && userDate < nextDate
      }).length

      // Count new scans for this day
      const newScansCount = scans.filter(scan => {
        const scanDate = scan.createdAt?.toDate?.() || new Date(scan.createdAt)
        return scanDate >= date && scanDate < nextDate
      }).length

      const dateStr = date.toLocaleDateString('tr-TR', { 
        month: 'short', 
        day: 'numeric' 
      })

      stats.newUsers.push({
        date: dateStr,
        value: newUsersCount
      })

      stats.newScans.push({
        date: dateStr,
        value: newScansCount
      })
    }

    return stats
  }

  async checkPremiumStatus(userId) {
    try {
      const response = await fetch('https://us-central1-sharinbite.cloudfunctions.net/checkPremiumStatusHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error checking premium status:', error)
      return null
    }
  }

  async updatePremiumExpiration(userId, expiresAt, startsAt) {
    try {
      const response = await fetch('https://us-central1-sharinbite.cloudfunctions.net/updatePremiumExpirationHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId,
          expiresAt: expiresAt,
          startsAt: startsAt
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating premium expiration:', error)
      throw error
    }
  }

  async revokePremiumAccess(userId) {
    try {
      const response = await fetch('https://us-central1-sharinbite.cloudfunctions.net/revokePremiumAccessHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error revoking premium access:', error)
      throw error
    }
  }
} 